/** What is waiting on the signed-in person - the one read behind the bell, Home's "Needs you" tile
 * and the "Waiting on you" tab of Action items (ADR-0212 decision 6c).
 *
 * **One definition, so three numbers cannot differ.** An item is waiting on a person when the work
 * feed says it is *theirs* (`mine=true`, the server's `assignee=me`: a decision that names them, a
 * task they own, a derived item whose named owner they are) or when it is a bill to review, which has
 * nobody named and is the work of whoever may upload. The feed is not re-queried per screen: Home, the
 * bell and the tab all read this hook, which is one query key, so a count in the bell is the count
 * under it.
 *
 * **The count is the server's.** It is the sum of the two `counts.total` values, which are over the
 * whole feed and not over the page that was fetched; the rows are the page. A cap on the page is said
 * (`truncated`), not hidden, and nothing here adds or subtracts a row.
 *
 * **Null is not zero.** A role that is never asked for anything - a Viewer or an Auditor with nothing
 * assigned - is shown a dash (`waitingText`), the same rule the home summary has for a count a role
 * is not asked (`SummaryCountsOut`). A failed read throws, so the bell says `?` and a tab says it
 * could not be read, and neither can look like "nothing is waiting".
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { useMe } from "./auth";
import { unwrap } from "./queries";
import type { components } from "./schema";

export type WaitingItem = components["schemas"]["WorkItemOut"];

/** The most items one read brings back (`limit` on `GET /work/items`). */
export const WAITING_PAGE = 200;

/** The roles that review a bill (`evidence.py` MAY_UPLOAD). The bills-to-review kind is derived for
 * these only (`workitems/kinds.py`), so the second read is not made for anyone else. */
const REVIEWS_BILLS: readonly string[] = ["admin", "approver", "contributor"];

export const waitingKeys = {
  mine: (role: string) => ["work", "waiting-on-me", role] as const,
};

export interface Waiting {
  items: WaitingItem[];
  /** The server's count, over everything waiting and not only the rows fetched. */
  total: number;
  overdue: number;
  /** More is waiting than the rows fetched. */
  truncated: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** A response is checked, not trusted: a stub or a proxy answering `[]` is a failed read. */
function readFeed(value: unknown): components["schemas"]["WorkItemsOut"] {
  if (!isRecord(value) || !Array.isArray(value.items) || !isRecord(value.counts)) {
    throw new Error("What is waiting on you came back in a shape this screen cannot read.");
  }
  return value as unknown as components["schemas"]["WorkItemsOut"];
}

/** Waiting on this person, for the role in force. Disabled until the role is known, because the
 * second read depends on it. */
export function useWaitingOnMe() {
  const { data: me } = useMe();
  const role = me?.role;
  return useQuery({
    queryKey: waitingKeys.mine(role ?? "none"),
    enabled: role !== undefined,
    retry: false,
    queryFn: async (): Promise<Waiting> => {
      const mine = readFeed(
        unwrap(
          await api.GET("/api/v1/work/items", {
            params: { query: { mine: true, limit: WAITING_PAGE } },
          }),
        ),
      );
      const bills =
        role !== undefined && REVIEWS_BILLS.includes(role)
          ? readFeed(
              unwrap(
                await api.GET("/api/v1/work/items", {
                  params: { query: { kind: ["document_to_review"], limit: WAITING_PAGE } },
                }),
              ),
            )
          : null;
      // A bill to review that someone has taken as a task is the task, and is already in `mine`.
      const taken = new Set(mine.items.map((item) => item.item_key));
      const billRows = (bills?.items ?? []).filter((item) => !taken.has(item.item_key));
      const items = [...mine.items, ...billRows];
      return {
        items,
        total:
          mine.counts.total +
          (bills ? bills.counts.total - (bills.items.length - billRows.length) : 0),
        overdue: items.filter((item) => item.overdue).length,
        truncated: mine.next_cursor !== null || (bills?.next_cursor ?? null) !== null,
      };
    },
  });
}

/** The count as it is printed: a dash for a role that is never asked and has nothing, the number
 * otherwise. `undefined` (still reading, or failed) is a dash too, because it is not a count. */
export function waitingText(role: string | undefined, total: number | undefined): string {
  if (total === undefined) return "-";
  if (total === 0 && role !== undefined && !REVIEWS_BILLS.includes(role)) return "-";
  return String(total);
}
